import { AlertCircle, Clock, Input } from "@ews-admin/global-design-system";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Facility } from "../../../../types/facility.definition";

interface OperatingHoursTabProps {
  facility: Facility;
  setFacility: React.Dispatch<React.SetStateAction<Facility>>;
}

interface TimeValidation {
  [key: string]: {
    isValid: boolean;
    message?: string;
  };
}

const daysOfWeek = [
  {
    key: "monday",
    translationKey: "facilitySettings.operatingHours.days.monday",
  },
  {
    key: "tuesday",
    translationKey: "facilitySettings.operatingHours.days.tuesday",
  },
  {
    key: "wednesday",
    translationKey: "facilitySettings.operatingHours.days.wednesday",
  },
  {
    key: "thursday",
    translationKey: "facilitySettings.operatingHours.days.thursday",
  },
  {
    key: "friday",
    translationKey: "facilitySettings.operatingHours.days.friday",
  },
  {
    key: "saturday",
    translationKey: "facilitySettings.operatingHours.days.saturday",
  },
  {
    key: "sunday",
    translationKey: "facilitySettings.operatingHours.days.sunday",
  },
];

export const OperatingHoursTab: React.FC<OperatingHoursTabProps> = ({
  facility,
  setFacility,
}) => {
  const { t } = useTranslation();
  const [validation, setValidation] = useState<TimeValidation>({});
  const [inputValues, setInputValues] = useState<{ [key: string]: string }>({});

  // Helper function to find hours for a specific day from the object format
  const getHoursForDay = useCallback(
    (day: string) => {
      if (!facility.hours || typeof facility.hours !== "object") return null;
      return facility.hours[day] || null;
    },
    [facility.hours]
  );

  // Helper function to update hours for a specific day in the object format
  const updateHoursForDay = useCallback(
    (day: string, hours: { open: string; close: string }) => {
      if (!facility.hours || typeof facility.hours !== "object") {
        return { [day]: hours };
      }

      return {
        ...facility.hours,
        [day]: hours,
      };
    },
    [facility.hours]
  );

  // Helper function to format hours for display
  const formatHours = useCallback(
    (hours: { open: string; close: string } | null) => {
      if (
        !hours ||
        hours.open === "Closed" ||
        hours.close === "Closed" ||
        hours.open === t("facilitySettings.operatingHours.closed") ||
        hours.close === t("facilitySettings.operatingHours.closed")
      ) {
        return t("facilitySettings.operatingHours.closed");
      }
      return `${hours.open} - ${hours.close}`;
    },
    [t]
  );

  // Initialize input values when facility data changes
  useEffect(() => {
    const initialValues: { [key: string]: string } = {};
    daysOfWeek.forEach((day) => {
      initialValues[day.key] = formatHours(getHoursForDay(day.key));
    });
    setInputValues(initialValues);
  }, [facility.hours, getHoursForDay, formatHours]);

  const validateTimeFormat = (
    value: string
  ): { isValid: boolean; message?: string } => {
    if (!value.trim()) {
      return {
        isValid: false,
        message: t(
          "facilitySettings.operatingHours.validation.pleaseEnterHours"
        ),
      };
    }

    if (
      value.toLowerCase() === "closed" ||
      value.toLowerCase() ===
      t("facilitySettings.operatingHours.closed").toLowerCase()
    ) {
      return { isValid: true };
    }

    // Check for HH:MM - HH:MM format
    const timeMatch = value.match(
      /^(\d{1,2}):(\d{2})\s*-\s*(\d{1,2}):(\d{2})$/
    );

    if (!timeMatch) {
      return {
        isValid: false,
        message: t("facilitySettings.operatingHours.validation.invalidFormat"),
      };
    }

    const [, openHour, openMinute, closeHour, closeMinute] = timeMatch;
    const openTime = parseInt(openHour) * 60 + parseInt(openMinute);
    const closeTime = parseInt(closeHour) * 60 + parseInt(closeMinute);

    // Validate hour ranges
    if (parseInt(openHour) > 23 || parseInt(closeHour) > 23) {
      return {
        isValid: false,
        message: t("facilitySettings.operatingHours.validation.invalidHours"),
      };
    }

    // Validate minute ranges
    if (parseInt(openMinute) > 59 || parseInt(closeMinute) > 59) {
      return {
        isValid: false,
        message: t("facilitySettings.operatingHours.validation.invalidMinutes"),
      };
    }

    // Check if close time is after open time
    if (closeTime <= openTime) {
      return {
        isValid: false,
        message: t(
          "facilitySettings.operatingHours.validation.closeBeforeOpen"
        ),
      };
    }

    return { isValid: true };
  };

  const handleOperatingHoursChange = (day: string, value: string) => {
    // Update the input value immediately for real-time typing
    setInputValues((prev) => ({
      ...prev,
      [day]: value,
    }));

    const validationResult = validateTimeFormat(value);

    setValidation((prev) => ({
      ...prev,
      [day]: validationResult,
    }));

    if (validationResult.isValid) {
      if (
        value.toLowerCase() === "closed" ||
        value.toLowerCase() ===
        t("facilitySettings.operatingHours.closed").toLowerCase()
      ) {
        const updatedHours = updateHoursForDay(day, {
          open: "Closed",
          close: "Closed",
        });
        setFacility((prev) => ({
          ...prev,
          hours: updatedHours,
        }));
      } else {
        const timeMatch = value.match(
          /^(\d{1,2}:\d{2})\s*-\s*(\d{1,2}:\d{2})$/
        );
        if (timeMatch) {
          const [, open, close] = timeMatch;
          const updatedHours = updateHoursForDay(day, { open, close });
          setFacility((prev) => ({
            ...prev,
            hours: updatedHours,
          }));
        }
      }
    }
  };

  const computeOperatingDays = () => {
    if (!facility.hours || typeof facility.hours !== "object") return 0;

    return Object.values(facility.hours).filter((dayHours) => {
      return (
        dayHours &&
        dayHours.open !== "Closed" &&
        dayHours.close !== "Closed" &&
        dayHours.open !== t("facilitySettings.operatingHours.closed") &&
        dayHours.close !== t("facilitySettings.operatingHours.closed")
      );
    }).length;
  };

  const getOperatingDaysText = () => {
    const days = computeOperatingDays();
    if (days === 0)
      return t("facilitySettings.operatingHours.noOperatingDaysSet");
    if (days === 1) return t("facilitySettings.operatingHours.oneOperatingDay");
    return t("facilitySettings.operatingHours.operatingDays", { count: days });
  };

  const getInputClassName = (day: string) => {
    const baseClasses =
      "pl-10 transition-all duration-200 focus:ring-2 focus:ring-primary-500 focus:border-primary-500";
    const dayValidation = validation[day];

    if (dayValidation && !dayValidation.isValid) {
      return `${baseClasses} border-red-300 focus:border-red-500 focus:ring-red-500`;
    }

    return baseClasses;
  };

  return (
    <div className="space-y-6">
      {/* Operating Days Summary */}
      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
          <span className="text-sm font-medium text-blue-900">
            {getOperatingDaysText()}
          </span>
        </div>
      </div>

      {/* Instructions */}
      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
        <h4 className="mb-2 text-sm font-medium text-gray-900">
          {t("facilitySettings.operatingHours.instructions")}
        </h4>
        <ul className="space-y-1 text-sm text-gray-600">
          <li>• {t("facilitySettings.operatingHours.formatExample")}</li>
          <li>• {t("facilitySettings.operatingHours.closedExample")}</li>
          <li>• {t("facilitySettings.operatingHours.hoursRange")}</li>
          <li>• {t("facilitySettings.operatingHours.closeAfterOpen")}</li>
        </ul>
      </div>

      {/* Operating Hours Form */}
      <div className="space-y-4">
        {daysOfWeek.map((day) => (
          <div key={day.key} className="space-y-2">
            <div className="flex items-center space-x-4">
              <div className="w-32 font-medium text-gray-700">
                {t(day.translationKey)}:
              </div>
              <div className="relative flex-1">
                <span className="flex absolute inset-y-0 left-0 items-center pl-3">
                  <Clock className="w-5 h-5 text-gray-400" />
                </span>
                <Input
                  value={
                    inputValues[day.key] !== undefined
                      ? inputValues[day.key]
                      : formatHours(getHoursForDay(day.key))
                  }
                  onChange={(e) =>
                    handleOperatingHoursChange(day.key, e.target.value)
                  }
                  placeholder={t("facilitySettings.operatingHours.placeholder")}
                  className={getInputClassName(day.key)}
                />
              </div>
            </div>

            {/* Validation Error */}
            {validation[day.key] && !validation[day.key].isValid && (
              <div className="flex items-center ml-32 space-x-2 text-sm text-red-600">
                <AlertCircle className="w-4 h-4" />
                <span>{validation[day.key].message}</span>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
        <h4 className="mb-3 text-sm font-medium text-gray-900">
          {t("facilitySettings.operatingHours.quickActions")}
        </h4>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => {
              // Update all input values first
              const newInputValues: { [key: string]: string } = {};
              daysOfWeek.forEach((day) => {
                newInputValues[day.key] = "08:00 - 17:00";
              });
              setInputValues(newInputValues);

              // Then update facility hours
              const updatedHours = { ...facility.hours };
              daysOfWeek.forEach((day) => {
                updatedHours[day.key] = { open: "08:00", close: "17:00" };
              });
              setFacility((prev) => ({
                ...prev,
                hours: updatedHours,
              }));
            }}
            className="px-3 py-1 text-sm text-blue-700 bg-blue-100 rounded-md transition-colors hover:bg-blue-200"
          >
            {t("facilitySettings.operatingHours.setAllWeekdays")}
          </button>
          <button
            type="button"
            onClick={() => {
              // Update all input values first
              const newInputValues: { [key: string]: string } = {};
              daysOfWeek.forEach((day) => {
                newInputValues[day.key] = "09:00 - 18:00";
              });
              setInputValues(newInputValues);

              // Then update facility hours
              const updatedHours = { ...facility.hours };
              daysOfWeek.forEach((day) => {
                updatedHours[day.key] = { open: "09:00", close: "18:00" };
              });
              setFacility((prev) => ({
                ...prev,
                hours: updatedHours,
              }));
            }}
            className="px-3 py-1 text-sm text-green-700 bg-green-100 rounded-md transition-colors hover:bg-green-200"
          >
            {t("facilitySettings.operatingHours.setAllDays")}
          </button>
          <button
            type="button"
            onClick={() => {
              // Update all input values first
              const newInputValues: { [key: string]: string } = {};
              daysOfWeek.forEach((day) => {
                newInputValues[day.key] = t(
                  "facilitySettings.operatingHours.closed"
                );
              });
              setInputValues(newInputValues);

              // Then update facility hours
              const updatedHours = { ...facility.hours };
              daysOfWeek.forEach((day) => {
                updatedHours[day.key] = { open: "Closed", close: "Closed" };
              });
              setFacility((prev) => ({
                ...prev,
                hours: updatedHours,
              }));
            }}
            className="px-3 py-1 text-sm text-red-700 bg-red-100 rounded-md transition-colors hover:bg-red-200"
          >
            {t("facilitySettings.operatingHours.closeAllDays")}
          </button>
        </div>
      </div>

      {/* Preview */}
      <div className="p-4 bg-white rounded-lg border border-gray-200">
        <h4 className="mb-3 text-sm font-medium text-gray-900">
          {t("facilitySettings.operatingHours.preview")}
        </h4>
        <div className="grid grid-cols-1 gap-2 text-sm md:grid-cols-2">
          {daysOfWeek.map((day) => (
            <div
              key={day.key}
              className="flex justify-between items-center py-1"
            >
              <span className="font-medium text-gray-700">
                {t(day.translationKey)}
              </span>
              <span className="text-gray-600">
                {formatHours(getHoursForDay(day.key))}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
